// Dono: B3 (organização). /etiquetas: gerenciar etiquetas (renomear, mesclar, apagar, contagens, origem).
import { useEffect, useRef, useState } from "preact/hooks";
import type { Tag } from "../../../../shared/types.ts";
import { normalizeTag } from "../../../../shared/normalize.ts";
import { IconPrev, IconTrash } from "../Icons.tsx";
import { backTo, failed, searchFor, store, toast } from "../state.ts";
import { Confirm } from "../spaces/common.tsx";
import { IconLock, IconMerge, IconPencil, IconSort } from "../spaces/icons.tsx";
import { cardsLabel, retagQuery } from "../spaces/order.ts";
import { PRIVATE_NOTE } from "../spaces/PrivateToggle.tsx";

const RESERVED = "privado";
const ORIGIN: Record<Tag["origin"], string> = { user: "Sua", ai: "IA", mixed: "Sua e IA" };
type Filter = "all" | "user" | "ai";
const FILTERS: [Filter, string][] = [["all", "Todas"], ["user", "Suas"], ["ai", "Da IA"]];
type Ask = { kind: "delete"; tags: Tag[] } | { kind: "merge"; tags: Tag[]; into?: string } | null;
const plural = (n: number, one: string, many: string) => (n === 1 ? `1 ${one}` : `${n} ${many}`);
const RESERVED_NAME = "Esse nome é reservado: os cards sairiam de Tudo, da busca e do Reencontro. Para esconder um card, use a chave Privado no detalhe dele.";

/** Coleções automáticas que buscam `#from` (normalizada) passam a buscar `#to`; devolve os ids que mudaram. */
async function retagSpaces(from: string, to: string): Promise<string[]> {
  const changed: string[] = [];
  for (const s of store.spaces.value) {
    const q = s.kind === "smart" && s.query ? retagQuery(s.query, from, to) : null;
    if (!q || q === s.query) continue;
    await store.updateSpace(s.id, { query: q });
    changed.push(s.id);
  }
  return changed;
}
const alsoSpaces = (n: number) => (n ? ` e em ${plural(n, "coleção automática", "coleções automáticas")}` : "");

export function TagsPage() {
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<"name" | "count">("count");
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [editing, setEditingState] = useState<string | null>(null);
  // Sair do campo (Enter, blur ao sumir, Esc) chama rename mais de uma vez: só a primeira vale; Esc cancela antes.
  const live = useRef<string | null>(null);
  const setEditing = (id: string | null) => ((live.current = id), setEditingState(id), setErr(""));
  const [ask, setAsk] = useState<Ask>(null);
  const [err, setErr] = useState("");
  const all = useRef<HTMLInputElement>(null);

  // Contagem por etiqueta: cards guardados (a lixeira não conta).
  const counts = new Map<string, number>();
  for (const c of store.cards.value) for (const t of c.tagIds) counts.set(t, (counts.get(t) ?? 0) + 1);
  const tags = [...store.tags.value.values()];
  // Sem nenhuma etiqueta da IA (a análise automática desligada, por exemplo), o filtro de origem não separa nada: some.
  const hasAi = tags.some((t) => t.origin !== "user");
  const shown: Filter = hasAi ? filter : "all";
  const rows = tags
    .filter((t) => shown === "all" || t.origin === shown || t.origin === "mixed")
    .sort((a, b) => (sort === "name" ? a.name.localeCompare(b.name, "pt-BR") : (counts.get(b.id) ?? 0) - (counts.get(a.id) ?? 0) || a.name.localeCompare(b.name, "pt-BR")));
  const editable = rows.filter((t) => t.normalized !== RESERVED);
  const chosen = tags.filter((t) => picked.has(t.id));
  const allOn = editable.length > 0 && editable.every((t) => picked.has(t.id));
  useEffect(() => void (all.current && (all.current.indeterminate = chosen.length > 0 && !allOn)), [chosen.length, allOn]);
  useEffect(() => void (editing && document.querySelector<HTMLInputElement>(".tt-input")?.select()), [editing]);
  // Etiqueta que sumiu (mesclada/apagada aqui ou em outro aparelho) sai da seleção.
  useEffect(() => void (chosen.length !== picked.size && setPicked(new Set(chosen.map((t) => t.id)))), [tags.length]);

  const pick = (id: string, on: boolean) => setPicked((p) => (on ? new Set(p).add(id) : new Set([...p].filter((x) => x !== id))));
  const rename = (t: Tag, value: string) => {
    if (live.current !== t.id) return;
    const name = value.trim();
    // "privado" esconde os cards: não vira nome de outra etiqueta por aqui (nem por mesclar). O campo fica aberto com o aviso.
    if (normalizeTag(name) === RESERVED) return setErr(RESERVED_NAME);
    setEditing(null);
    if (!name || name === t.name) return;
    const other = tags.find((x) => x.id !== t.id && x.normalized === normalizeTag(name));
    if (other) return setAsk({ kind: "merge", tags: [t, other], into: other.id }); // nome de outra: o caminho é mesclar
    const n = counts.get(t.id) ?? 0;
    store
      .renameTag(t.id, name)
      .then(async () => {
        if (store.tags.value.get(t.id)?.name !== name) return; // recusada (o store já avisou)
        const moved = normalizeTag(name) === t.normalized ? [] : await retagSpaces(t.normalized, name);
        toast({ key: `ren:${t.id}`, text: `Etiqueta renomeada para “${name}” em ${cardsLabel(n)}${alsoSpaces(moved.length)}.` });
      })
      .catch(failed);
  };
  const sortHead = (key: "name" | "count", label: string) => (
    <th scope="col" aria-sort={sort === key ? (key === "name" ? "ascending" : "descending") : undefined} class={key === "count" ? "num" : undefined}>
      <button type="button" class="tt-sort" onClick={() => setSort(key)}>
        {label}
        <IconSort />
      </button>
    </th>
  );

  return (
    <main id="main" tabIndex={-1} class="page organize tags-page">
      <header class="page-head org-head">
        <a class="round-back" href="/" onClick={backTo("/")} aria-label="Voltar para Tudo">
          <IconPrev />
        </a>
        <h1>Etiquetas</h1>
      </header>
      <p class="tags-intro">Mudar uma etiqueta aqui muda todos os cards que a usam e as coleções automáticas que buscam por ela. Apagar uma etiqueta não apaga cards.</p>
      <p class="tags-private">
        <IconLock />
        <span>
          <b>privado</b> é uma etiqueta reservada. {PRIVATE_NOTE}
        </span>
      </p>
      <div class="tags-bar">
        {hasAi && (
          <div class="tags-filter" role="group" aria-label="Origem das etiquetas">
            {FILTERS.map(([f, label]) => (
              <button type="button" key={f} aria-pressed={filter === f} onClick={() => setFilter(f)}>
                {label}
              </button>
            ))}
          </div>
        )}
        {chosen.length > 0 && (
          <div class="tags-acts">
            <span class="tags-picked" aria-live="polite">
              {plural(chosen.length, "selecionada", "selecionadas")}
            </span>
            <button type="button" class="org-btn small quiet" disabled={chosen.length < 2} onClick={() => setAsk({ kind: "merge", tags: chosen })}>
              <IconMerge />
              Mesclar
            </button>
            <button type="button" class="org-btn small danger" onClick={() => setAsk({ kind: "delete", tags: chosen })}>
              <IconTrash />
              Apagar
            </button>
          </div>
        )}
      </div>
      {rows.length === 0 ? (
        <div class="empty">
          <p class="empty-title">{tags.length ? "Nenhuma etiqueta aqui." : "Nenhuma etiqueta ainda."}</p>
          <p class="empty-line">{hasAi ? "Etiquetas aparecem quando você as põe num card ou quando a IA sugere." : "Etiquetas aparecem quando você as põe num card."}</p>
        </div>
      ) : (
        <table class="tag-table">
          <caption class="sr-only">Etiquetas com origem e número de cards. Os botões de coluna ordenam.</caption>
          <thead>
            <tr>
              <th scope="col" class="tt-check">
                {/* O rótulo em volta dá ao quadradinho um alvo de toque de 44 px. */}
                <label class="tt-hit">
                  <input
                    ref={all}
                    type="checkbox"
                    aria-label="Selecionar todas as etiquetas"
                    checked={allOn}
                    onChange={(e) => setPicked(e.currentTarget.checked ? new Set(editable.map((t) => t.id)) : new Set())}
                  />
                </label>
              </th>
              {sortHead("name", "Nome")}
              <th scope="col" class="tt-origin">
                Origem
              </th>
              {sortHead("count", "Cards")}
              <th scope="col" class="tt-acts">
                <span class="sr-only">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => {
              const reserved = t.normalized === RESERVED;
              const n = counts.get(t.id) ?? 0;
              return (
                <tr key={t.id} data-picked={picked.has(t.id) || undefined}>
                  <td class="tt-check">
                    {!reserved && (
                      <label class="tt-hit">
                        <input type="checkbox" aria-label={`Selecionar a etiqueta ${t.name}`} checked={picked.has(t.id)} onChange={(e) => pick(t.id, e.currentTarget.checked)} />
                      </label>
                    )}
                  </td>
                  <th scope="row" class="tt-name">
                    {editing === t.id ? (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          rename(t, (e.currentTarget.elements.namedItem("name") as HTMLInputElement).value);
                        }}
                      >
                        <input
                          name="name"
                          class="tt-input"
                          aria-label={`Novo nome para ${t.name}`}
                          aria-invalid={err ? "true" : undefined}
                          aria-describedby={err ? "tt-error" : undefined}
                          defaultValue={t.name}
                          maxLength={80}
                          autocomplete="off"
                          autocapitalize="none"
                          autocorrect="off"
                          spellcheck={false}
                          onInput={() => setErr("")}
                          onBlur={(e) => rename(t, e.currentTarget.value)}
                          onKeyDown={(e) => {
                            if (e.key !== "Escape") return;
                            e.preventDefault();
                            setEditing(null);
                            requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-rename="${CSS.escape(t.id)}"]`)?.focus());
                          }}
                        />
                        {err && (
                          <p id="tt-error" class="tt-error" role="alert">
                            {err}
                          </p>
                        )}
                      </form>
                    ) : (
                      <a href="/" class="tt-link" title="Buscar esta etiqueta" onClick={(e) => (e.preventDefault(), searchFor(`#${t.name.includes(" ") ? `"${t.name}"` : t.name}`))}>
                        {t.name}
                      </a>
                    )}
                    {reserved && (
                      <span class="tt-reserved">
                        <IconLock />
                        reservada
                      </span>
                    )}
                    <span class="tt-origin-inline">{ORIGIN[t.origin]}</span>
                  </th>
                  <td class="tt-origin">{ORIGIN[t.origin]}</td>
                  <td class="num">{n}</td>
                  <td class="tt-acts">
                    {!reserved && (
                      <>
                        <button type="button" class="tt-btn" data-rename={t.id} aria-label={`Renomear a etiqueta ${t.name}`} data-tip="Renomear" onClick={() => setEditing(t.id)}>
                          <IconPencil />
                        </button>
                        <button type="button" class="tt-btn" aria-label={`Apagar a etiqueta ${t.name}`} data-tip="Apagar" onClick={() => setAsk({ kind: "delete", tags: [t] })}>
                          <IconTrash />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {ask?.kind === "delete" && (
        <Confirm
          title={ask.tags.length === 1 ? `Apagar a etiqueta “${ask.tags[0].name}”?` : `Apagar ${ask.tags.length} etiquetas?`}
          line="Os cards continuam guardados; só perdem esta etiqueta. Não dá para desfazer."
          action="Apagar"
          danger
          onConfirm={async () => {
            for (const t of ask.tags) await store.deleteTag(t.id);
            setPicked(new Set());
            toast({ key: `tdel:${Date.now()}`, text: ask.tags.length === 1 ? "Etiqueta apagada. Os cards ficaram." : `${ask.tags.length} etiquetas apagadas. Os cards ficaram.` });
          }}
          onClose={() => setAsk(null)}
        >
          {ask.tags.length > 1 && <TagList tags={ask.tags} counts={counts} />}
        </Confirm>
      )}
      {ask?.kind === "merge" && <MergeAsk ask={ask} counts={counts} onDone={() => setPicked(new Set())} onClose={() => setAsk(null)} />}
    </main>
  );
}

const TagList = ({ tags, counts }: { tags: Tag[]; counts: Map<string, number> }) => (
  <ul class="ask-tags">
    {tags.map((t) => (
      <li key={t.id}>
        {t.name} <span>{counts.get(t.id) ?? 0}</span>
      </li>
    ))}
  </ul>
);

/** Mesclar: escolhe a etiqueta que fica (a mais usada já vem marcada); as outras passam os cards para ela e somem. */
function MergeAsk({ ask, counts, onDone, onClose }: { ask: { tags: Tag[]; into?: string }; counts: Map<string, number>; onDone: () => void; onClose: () => void }) {
  const best = ask.into ?? [...ask.tags].sort((a, b) => (counts.get(b.id) ?? 0) - (counts.get(a.id) ?? 0))[0].id;
  const [into, setInto] = useState(best);
  const target = ask.tags.find((t) => t.id === into)!;
  return (
    <Confirm
      title={ask.into ? `“${target.name}” já existe` : `Mesclar ${ask.tags.length} etiquetas`}
      line={ask.into ? `Mesclar “${ask.tags[0].name}” em “${target.name}”? Os cards de uma passam a usar a outra.` : "Escolha a que fica. As outras somem e os cards delas passam a usar esta."}
      action="Mesclar"
      onConfirm={async () => {
        const others = ask.tags.filter((t) => t.id !== into);
        const moved = new Set<string>();
        for (const t of others) {
          await store.mergeTag(t.id, into);
          for (const id of await retagSpaces(t.normalized, target.name)) moved.add(id);
        }
        onDone();
        toast({ key: `merge:${Date.now()}`, text: `${plural(others.length, "etiqueta mesclada", "etiquetas mescladas")} em “${target.name}”${alsoSpaces(moved.size)}.` });
      }}
      onClose={onClose}
    >
      {!ask.into && (
        <fieldset class="ask-into">
          <legend class="sr-only">Etiqueta que fica</legend>
          {ask.tags.map((t) => (
            <label key={t.id}>
              <input type="radio" name="merge-into" checked={into === t.id} onChange={() => setInto(t.id)} />
              {t.name}
              <span>{cardsLabel(counts.get(t.id) ?? 0)}</span>
            </label>
          ))}
        </fieldset>
      )}
    </Confirm>
  );
}
